import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Info } from "@phosphor-icons/react";

const MODES = [
  { k: "free", label: "🆓 Livraison gratuite", desc: "Tous les frais d'envoi sont à votre charge — vos fans ne paient jamais la livraison." },
  { k: "flat", label: "📦 Tarif fixe", desc: "Un montant fixe est ajouté à chaque commande (indépendamment du nombre d'articles)." },
  { k: "per_item", label: "🧾 Par article", desc: "Un montant fixe est ajouté par article commandé (utile pour les articles lourds)." },
  { k: "free_over", label: "🎁 Gratuit au-dessus de X$", desc: "Frais fixes en dessous du seuil, livraison gratuite au-dessus." },
];

export default function ShippingTab({ user, refresh }) {
  const cfg = user?.shipping_config || {};
  const [form, setForm] = useState({
    shipping_mode: cfg.shipping_mode || "free",
    flat_rate: cfg.flat_rate != null ? cfg.flat_rate : 10,
    per_item_rate: cfg.per_item_rate != null ? cfg.per_item_rate : 5,
    free_threshold: cfg.free_threshold != null ? cfg.free_threshold : 75,
    shipping_note: cfg.shipping_note || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.shipping_config) return;
    setForm({
      shipping_mode: cfg.shipping_mode || "free",
      flat_rate: cfg.flat_rate != null ? cfg.flat_rate : 10,
      per_item_rate: cfg.per_item_rate != null ? cfg.per_item_rate : 5,
      free_threshold: cfg.free_threshold != null ? cfg.free_threshold : 75,
      shipping_note: cfg.shipping_note || "",
    });
    // eslint-disable-next-line
  }, [user]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        shipping_mode: form.shipping_mode,
        flat_rate: parseFloat(form.flat_rate) || 0,
        per_item_rate: parseFloat(form.per_item_rate) || 0,
        free_threshold: parseFloat(form.free_threshold) || 0,
        shipping_note: form.shipping_note || "",
      };
      await api.put("/artists/me/shipping", payload);
      toast.success("Frais de livraison enregistrés");
      refresh && refresh();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  // Live preview calculation
  const example = (subtotal, qty) => {
    const rate = parseFloat(form.flat_rate) || 0;
    const perItem = parseFloat(form.per_item_rate) || 0;
    const thr = parseFloat(form.free_threshold) || 0;
    if (form.shipping_mode === "free") return 0;
    if (form.shipping_mode === "flat") return rate;
    if (form.shipping_mode === "per_item") return perItem * qty;
    if (form.shipping_mode === "free_over") return subtotal >= thr ? 0 : rate;
    return 0;
  };

  const mode = MODES.find((m) => m.k === form.shipping_mode) || MODES[0];

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6" data-testid="shipping-tab">
      <form onSubmit={save} className="border border-white/10 p-6 bg-[#0A0A0A]" data-testid="shipping-form">
        <div className="text-xs uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2"><Truck size={14} /> Configuration livraison</div>
        <p className="text-xs text-white/50 mb-4">
          Choisissez comment vous facturez les frais d'envoi à vos fans. Le montant est ajouté automatiquement au checkout et versé directement à vous (aucune commission dessus).
        </p>

        <div className="space-y-2 mb-6">
          {MODES.map((m) => (
            <label key={m.k}
              className={`block border p-4 cursor-pointer transition-colors ${form.shipping_mode === m.k ? "border-[#FF3B00] bg-[#FF3B00]/5" : "border-white/20 hover:border-white/40"}`}>
              <input type="radio" name="mode" value={m.k}
                checked={form.shipping_mode === m.k}
                onChange={upd("shipping_mode")}
                data-testid={`ship-mode-${m.k}`}
                className="sr-only" />
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-medium">{m.label}</div>
                {form.shipping_mode === m.k && <span className="text-[10px] uppercase tracking-widest text-[#FF3B00]">Sélectionné</span>}
              </div>
              <p className="mt-1 text-xs text-white/50">{m.desc}</p>
            </label>
          ))}
        </div>

        {(form.shipping_mode === "flat" || form.shipping_mode === "free_over") && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-widest text-white/60">Tarif fixe (CAD)</label>
            <input type="number" step="0.5" min="0" value={form.flat_rate} onChange={upd("flat_rate")} data-testid="ship-flat-rate"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
          </div>
        )}

        {form.shipping_mode === "per_item" && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-widest text-white/60">Tarif par article (CAD)</label>
            <input type="number" step="0.5" min="0" value={form.per_item_rate} onChange={upd("per_item_rate")} data-testid="ship-per-item"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
          </div>
        )}

        {form.shipping_mode === "free_over" && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-widest text-white/60">Seuil pour livraison gratuite (CAD)</label>
            <input type="number" step="5" min="0" value={form.free_threshold} onChange={upd("free_threshold")} data-testid="ship-threshold"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
            <p className="mt-1 text-xs text-white/40">Ex: 75$ → livraison gratuite dès que le fan dépense 75$ ou plus chez vous.</p>
          </div>
        )}

        <div className="mb-6">
          <label className="text-xs uppercase tracking-widest text-white/60">Note aux acheteurs (optionnel)</label>
          <input value={form.shipping_note} onChange={upd("shipping_note")}
            placeholder="Ex: Expédié depuis Montréal, 5-7 jours ouvrables"
            className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3 text-sm" />
        </div>

        <button type="submit" disabled={saving} data-testid="ship-save"
          className="w-full py-3 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50">
          {saving ? "..." : "Enregistrer"}
        </button>
      </form>

      <div className="border border-white/10 p-6 bg-[#0A0A0A] h-fit">
        <div className="text-xs uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2"><Info size={14} /> Simulation</div>
        <p className="text-xs text-white/50 mb-4">
          Voici ce que vos fans verront au checkout selon le mode <span className="text-[#FF3B00]">{mode.label}</span> :
        </p>

        <div className="space-y-3">
          {[
            { label: "1 t-shirt à 30$", sub: 30, qty: 1 },
            { label: "2 hoodies à 45$ chacun", sub: 90, qty: 2 },
            { label: "3 articles à 25$ chacun", sub: 75, qty: 3 },
            { label: "Panier moyen 120$", sub: 120, qty: 3 },
          ].map((ex, i) => {
            const ship = example(ex.sub, ex.qty);
            const total = ex.sub + ship;
            return (
              <div key={i} className="border border-white/10 p-3 text-sm">
                <div className="text-xs text-white/60 mb-2">{ex.label}</div>
                <div className="flex justify-between"><span className="text-white/50">Sous-total</span><span>${ex.sub.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Livraison</span>
                  <span className={ship === 0 ? "text-green-400" : "text-[#FF3B00]"}>
                    {ship === 0 ? "GRATUITE" : `$${ship.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between font-display text-lg mt-1 border-t border-white/10 pt-1">
                  <span>Total</span><span>${total.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 border border-[#FF3B00]/30 bg-[#FF3B00]/5 p-3 text-xs text-white/70">
          💡 <span className="text-[#FF3B00] font-bold">Astuce</span> : le mode "Gratuit au-dessus de X$" augmente le panier moyen — les fans ajoutent souvent un article de plus pour économiser les frais.
        </div>
      </div>
    </div>
  );
}
